/**
 * `.rl-detail` — the right-hand column of Edit › Reels: which reel is being
 * edited, what is on it, and everything that can be done to it (CAE-1552).
 */
import Box from '@mui/material/Box';
import type { Asset } from '../../../../api/assets';
import type {
  ContestDetailReelOption,
  ContestFixture,
  ReelKind as ReelKindValue,
} from '../../../../api/contests';
import { MIN_ENABLED_OPTIONS_PER_REEL } from '../../../../contests/readiness';
import { ReelWarningBanner } from './reel-warning-banner';
import { ReelDetailHead } from './reel-detail-head';
import { ReelEmpty } from './reel-empty';
import { ReelKind } from './reel-kind';
import { ReelOptionTable } from './reel-option-table';
import { isShortOfEnabledOptions, type ReelSlot } from './reel-slots';

export interface ReelDetailProps {
  slot: ReelSlot;
  id: string;
  /** The rail tab that opened this panel. */
  labelledBy: string;
  fixturesById: ReadonlyMap<string, ContestFixture>;
  assetsById: ReadonlyMap<string, Asset>;
  editable: boolean;
  busy: boolean;
  /** The kind this slot's Reel will be created as, while it does not exist. */
  newReelKind: ReelKindValue;
  onChooseKind: (kind: ReelKindValue) => void;
  onAddOutcomes: () => void;
  onRename: (label: string) => void;
  onToggleEnabled: (option: ContestDetailReelOption, enabled: boolean) => void;
  onMoveTo: (option: ContestDetailReelOption, position: number) => void;
  onPickAsset: (option: ContestDetailReelOption) => void;
  onRemove: (option: ContestDetailReelOption) => void;
  onEditDisplayName: (option: ContestDetailReelOption) => void;
  onEditParticipant: (option: ContestDetailReelOption) => void;
  onClearAsset: (option: ContestDetailReelOption) => void;
}

export const ReelDetail = ({
  slot,
  id,
  labelledBy,
  fixturesById,
  assetsById,
  editable,
  busy,
  newReelKind,
  onChooseKind,
  onAddOutcomes,
  onRename,
  onToggleEnabled,
  onMoveTo,
  onPickAsset,
  onRemove,
  onEditDisplayName,
  onEditParticipant,
  onClearAsset,
}: ReelDetailProps) => {
  /*
   * A reel above the contest's reel count is read-only, whatever the contest's
   * status says (CAE-1591). Every write this panel offers goes through a route
   * guarded by `assertReelNumber(reelNumber, reelCount)` — add outcomes, rename,
   * toggle, reorder, pick or clear an asset, set a club or number, remove — so
   * on a stranded reel they all come back `reel N is outside the contest's M
   * reels`. Drawing them live
   * offers the operator a shelf of buttons that cannot work, and the two that
   * read as the fix ("+ Add outcomes", the empty state's CTA) are the furthest
   * from it: the fix is the reel count back up, on Basic information.
   */
  const live = editable && !slot.beyondReelCount;

  return (
    <Box
      role="tabpanel"
      id={id}
      aria-labelledby={labelledBy}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '18px',
        padding: '24px 28px',
        minWidth: 0,
      }}
    >
      <ReelDetailHead
        slot={slot}
        fixturesById={fixturesById}
        editable={live}
        onAddOutcomes={onAddOutcomes}
        onRename={onRename}
      />

      <ReelKind
        existingKind={slot.reel?.kind}
        newReelKind={newReelKind}
        editable={live}
        onChoose={onChooseKind}
      />

      {/*
       * The stranded reel leads, and alone — it is the only one of these whose
       * fix is on another step, and the only one where the controls below it are
       * dead. Its wording is the rail's `beyond reel N` said long, and the
       * Setup Rail's "set the reel count back to N" said in the second person.
       */}
      {slot.beyondReelCount ? (
        <ReelWarningBanner
          headline={`${slot.title} is beyond this contest's reels.`}
          detail="It was built while the reel count was higher, and nothing on it can be edited now. The contest cannot go live while it exists — raise the reel count on Basic information to bring it back."
        />
      ) : null}
      {/*
       * Both faults, and a reel can carry both at once — a reel of five with one
       * enabled and no artwork on any of them is two separate things to fix, and
       * one banner swallowing the other would send the operator back for a second
       * trip. The enabled one leads: it blocks publishing outright, where missing
       * artwork blocks it with a thumbnail that can follow later.
       */}
      {/*
       * Not on a reel with nothing on it, even though it is short by the same
       * count: `ReelEmpty` below already says a reel needs two enabled options,
       * and a banner saying it again directly above would be the same sentence
       * twice over two paragraphs. The rail still marks the reel amber, which is
       * what keeps it level with the Setup Rail.
       *
       * And not on a stranded reel either, whatever is on it. The rail says
       * `beyond reel N` and nothing else about it, for the reason `reelFaults`
       * gives; a banner here asking for two enabled options would be this panel
       * contradicting the rail an inch to its left — and asking for the one
       * thing the backend will not accept on this reel. The banner above says
       * what it is instead.
       */}
      {!slot.beyondReelCount && slot.stats.options > 0 && isShortOfEnabledOptions(slot) ? (
        <ReelWarningBanner
          headline={`${slot.title} needs at least ${MIN_ENABLED_OPTIONS_PER_REEL} enabled options.`}
          detail="A reel is a choice between options, and one option is not a choice — switch more on, or add outcomes."
        />
      ) : null}
      {!slot.beyondReelCount && slot.stats.missingAssets > 0 ? (
        <ReelWarningBanner
          headline={
            slot.stats.missingAssets === 1
              ? '1 enabled option is missing an asset.'
              : `${slot.stats.missingAssets} enabled options are missing assets.`
          }
          detail="Players won't see a thumbnail until you upload one — click the image slot on each row."
        />
      ) : null}
      {slot.stats.options === 0 ? (
        /*
         * A stranded reel gets no empty state: its CTA is `+ Add outcomes`, the
         * one request `assertReelNumber` is certain to refuse. The banner above
         * has already named the reel and said what to do about it, so there is
         * nothing left for this block to add.
         */
        slot.beyondReelCount ? null : (
          <ReelEmpty reelTitle={slot.title} editable={live} onAddOutcomes={onAddOutcomes} />
        )
      ) : (
        <ReelOptionTable
          options={slot.reel?.options ?? []}
          fixturesById={fixturesById}
          assetsById={assetsById}
          editable={live}
          busy={busy}
          onToggleEnabled={onToggleEnabled}
          onMoveTo={onMoveTo}
          onPickAsset={onPickAsset}
          onRemove={onRemove}
          onEditDisplayName={onEditDisplayName}
          onEditParticipant={onEditParticipant}
          onClearAsset={onClearAsset}
          reelKind={slot.reel?.kind ?? null}
        />
      )}
    </Box>
  );
};
